import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useUserContext } from '../../../context/PermissionsContext';
import { getFilteredTags } from '../../../utils/getFilteredTags';
import {
  GET_CYBER_LOCATION_LIST,
  LocationLevelType,
} from '../../cyber/location-applications';

// Constants for repeated string literals
const LOCATION_PARAM_KEY = 'location';
const ENTERPRISE_LOCATION_VALUE = 'Enterprise';

type LocationOption = {
  location: string;
  applications: number;
  uuid: string;
};

type CyberLocationListResponse = {
  getCyberLocationList: LocationLevelType[];
};

/**
 * Custom hook for managing location selection with URL synchronization
 *
 * This hook handles location data fetching, filtering based on user permissions,
 * URL synchronization with search parameters, and location selection state management.
 *
 * @returns Hook state and handlers for location selection management
 */
export const useCyberHubLocationSelector = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: cyberLocationListData } = useQuery<CyberLocationListResponse>(
    GET_CYBER_LOCATION_LIST,
  );
  const { isLocation, selectedTags } = useUserContext();

  const filteredLocations = getFilteredTags({
    data: cyberLocationListData?.getCyberLocationList,
    condition: isLocation,
    selectedTags,
  });

  /**
   * Transform filtered LocationLevelType objects into LocationOption format for the dropdown
   *
   * Takes the filtered locations (based on user permissions) and:
   * 1. Maps each LocationLevelType to a LocationOption with { location, applications, uuid }
   * 2. Sorts them alphabetically by location name
   * 3. Defaults to empty array if no filtered locations exist
   */
  const locations: LocationOption[] = useMemo(() => {
    return (
      filteredLocations
        ?.map((location: LocationLevelType) => ({
          location: location.locationName,
          applications: location.applictionsMapped || 0,
          uuid: location.locationUUID,
        }))
        .sort((a: LocationOption, b: LocationOption) =>
          a.location.localeCompare(b.location),
        ) || []
    );
  }, [filteredLocations]);

  const locationUuid = searchParams.get(LOCATION_PARAM_KEY);
  const [selectedLocation, setSelectedLocation] = useState(
    ENTERPRISE_LOCATION_VALUE,
  );

  useEffect(() => {
    const locationUuid = searchParams.get(LOCATION_PARAM_KEY);

    if (!locationUuid) {
      // No location param — reset to default
      setSelectedLocation(ENTERPRISE_LOCATION_VALUE);
      return;
    }

    // Wait for locations data to be available before trying to match
    if (!locations || locations.length === 0) {
      return;
    }
    const matchedLocation = locations.find(
      (loc: LocationOption) => loc.uuid === locationUuid,
    );

    if (matchedLocation) {
      setSelectedLocation(matchedLocation.location);
    } else {
      // Invalid UUID — reset to default and clean up URL
      setSelectedLocation(ENTERPRISE_LOCATION_VALUE);
      searchParams.delete(LOCATION_PARAM_KEY);
      setSearchParams(searchParams);
    }
  }, [searchParams, locations, setSearchParams]);

  const handleLocationChange = (newValue: string) => {
    setSelectedLocation(newValue);

    const isEnterprise = newValue === ENTERPRISE_LOCATION_VALUE;

    if (isEnterprise) {
      // Reset URL if user selects the default option
      searchParams.delete(LOCATION_PARAM_KEY);
      setSearchParams(searchParams);
      return;
    }

    const matched = locations.find(
      (loc: LocationOption) => loc.location === newValue,
    );

    if (matched) {
      // Update URL with selected location's UUID
      searchParams.set(LOCATION_PARAM_KEY, matched.uuid);
      setSearchParams(searchParams);
    }
  };

  return {
    locations,
    selectedLocation,
    locationUuid,
    handleLocationChange,
  };
};
